import { useEffect, useState } from "react";
import { Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

export function StarRow({ value, onChange, size = 20, readOnly = false, testidPrefix = "star" }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={readOnly}
            onMouseEnter={() => !readOnly && setHover(n)}
            onMouseLeave={() => !readOnly && setHover(0)}
            onClick={() => !readOnly && onChange && onChange(n)}
            className={`${readOnly ? "cursor-default" : "cursor-pointer hover:scale-110"} transition-transform`}
            data-testid={`${testidPrefix}-${n}`}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
          >
            <Star
              width={size}
              height={size}
              strokeWidth={1.5}
              className={active ? "text-[#E63946] fill-[#E63946]" : "text-black/25"}
            />
          </button>
        );
      })}
    </div>
  );
}

/**
 * Reusable reviews block — composer + list + summary. Works standalone (DealDetail) or inside a modal.
 * Props:
 *   - deal: {id, title, ...}
 *   - active: boolean — when false, skips fetching (used by the modal to only load when open)
 *   - onSummaryChange({avg_rating, review_count})
 */
export default function ReviewsSection({ deal, active = true, onSummaryChange, variant = "inline" }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [avg, setAvg] = useState(0);
  const [count, setCount] = useState(0);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const myReview = user ? reviews.find((r) => r.user_id === user.id) : null;

  const fetchReviews = async () => {
    if (!deal?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/deals/${deal.id}/reviews`);
      setReviews(data.reviews || []);
      setAvg(data.avg_rating || 0);
      setCount(data.review_count || 0);
      if (onSummaryChange) onSummaryChange({ avg_rating: data.avg_rating || 0, review_count: data.review_count || 0 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (active) fetchReviews();
  }, [active, deal?.id]);

  useEffect(() => {
    if (myReview) {
      setRating(myReview.rating);
      setComment(myReview.comment);
    } else {
      setRating(0);
      setComment("");
    }
  }, [myReview?.id, myReview?.rating, myReview?.comment]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please log in to leave a review."); return; }
    if (!rating) { toast.error("Pick a star rating first."); return; }
    if (!comment.trim()) { toast.error("Add a quick one-liner."); return; }
    setSubmitting(true);
    try {
      await api.post(`/deals/${deal.id}/reviews`, { rating, comment: comment.trim() });
      toast.success(myReview ? "Review updated." : "Review posted.");
      await fetchReviews();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const removeMine = async () => {
    if (!user) return;
    setSubmitting(true);
    try {
      await api.delete(`/deals/${deal.id}/reviews/mine`);
      setRating(0);
      setComment("");
      toast.success("Review removed.");
      await fetchReviews();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const isModal = variant === "modal";

  return (
    <div data-testid="reviews-section">
      {/* Summary */}
      <div className={`flex items-center gap-3 ${isModal ? "px-6 pt-2 pb-4" : "mb-4"}`}>
        <StarRow value={Math.round(avg)} readOnly size={16} testidPrefix="avg-star" />
        <span className="text-xs font-mono uppercase tracking-widest text-black/60" data-testid="reviews-summary">
          {count > 0 ? `${avg.toFixed(1)} · ${count} ${count === 1 ? "review" : "reviews"}` : "No reviews yet"}
        </span>
      </div>

      {/* Composer */}
      <div className={`${isModal ? "px-6 py-5 border-y border-black/10 bg-[#FAFAFA]" : "border border-black/10 p-5 bg-[#FAFAFA] my-6"}`}>
        {user ? (
          <form onSubmit={submit} data-testid="review-form">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 mb-2">
              {myReview ? "Update your review" : "Leave a one-liner"}
            </div>
            <div className="flex items-center gap-3 mb-3">
              <StarRow value={rating} onChange={setRating} testidPrefix="input-star" />
              {rating > 0 && (
                <span className="text-xs font-mono uppercase tracking-widest text-[#E63946]">{rating}/5</span>
              )}
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 180))}
              placeholder="Delivered as promised? Worth it? Say it in a sentence."
              rows={2}
              className="w-full border border-black/20 focus:border-black outline-none px-3 py-2 font-serif text-sm bg-white resize-none"
              data-testid="review-comment-input"
            />
            <div className="flex items-center justify-between mt-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-black/40">
                {comment.length}/180
              </div>
              <div className="flex items-center gap-2">
                {myReview && (
                  <button
                    type="button"
                    onClick={removeMine}
                    disabled={submitting}
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-black/50 hover:text-[#E63946]"
                    data-testid="review-delete-btn"
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={2} />
                    Delete
                  </button>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#111111] text-white text-[11px] font-bold uppercase tracking-[0.25em] px-4 py-2 hover:bg-[#E63946] disabled:opacity-50"
                  data-testid="review-submit-btn"
                >
                  {submitting ? "Saving…" : myReview ? "Update" : "Post review"}
                </button>
              </div>
            </div>
          </form>
        ) : (
          <div className="text-sm text-black/60 font-serif italic" data-testid="review-login-prompt">
            Log in to leave a star rating and a one-liner. Your take helps the community sift the real deals from the noise.
          </div>
        )}
      </div>

      {/* Review list */}
      <div className={isModal ? "max-h-[320px] overflow-y-auto" : ""}>
        {loading ? (
          <div className="p-6 text-xs font-mono uppercase tracking-widest text-black/40">Loading reviews…</div>
        ) : reviews.length === 0 ? (
          <div className={`${isModal ? "p-8" : "p-6 border border-dashed border-black/15"} text-center text-black/50 font-serif italic`} data-testid="reviews-empty">
            Be the first to weigh in on this deal.
          </div>
        ) : (
          <ul data-testid="reviews-list">
            {reviews.map((r) => (
              <li key={r.id} className={`${isModal ? "px-6" : ""} py-4 border-b border-black/5 last:border-0`} data-testid={`review-item-${r.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StarRow value={r.rating} readOnly size={13} testidPrefix={`review-stars-${r.id}`} />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-black/60">{r.user_name}</span>
                    {r.verified && (
                      <span
                        className="inline-flex items-center gap-1 bg-[#111111] text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5"
                        data-testid={`review-verified-${r.id}`}
                        title="Verified buyer — bookmarked or clicked through this deal"
                      >
                        <span aria-hidden="true">✓</span> Verified Buyer
                      </span>
                    )}
                  </div>
                  <span className="text-[9px] font-mono uppercase tracking-widest text-black/30">
                    {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </span>
                </div>
                <p className="text-sm font-serif text-black/80 leading-snug">{r.comment}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
